import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface ScannedOrder {
  orderId: string;
  date: string;
  client: string;
  phone: string;
  clientId: string;
  items: string[];
  total: number;
  bonusUsed: number;
  deliveryType: string;
  paymentType: string;
  status: string;
  tableNumber?: number;
}

const HISTORY_KEY = 'waiter-scanned-orders';
const MAX_HISTORY = 50;

function loadHistory(): ScannedOrder[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveToHistory(order: ScannedOrder): ScannedOrder[] {
  const history = loadHistory();
  history.unshift(order);
  const trimmed = history.slice(0, MAX_HISTORY);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(trimmed));
  return trimmed;
}

function removeFromHistory(orderId: string): ScannedOrder[] {
  const history = loadHistory().filter(o => o.orderId !== orderId);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
  return history;
}

export const QRScannerPage: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<ScannedOrder | null>(null);
  const [history, setHistory] = useState<ScannedOrder[]>(loadHistory);
  const scannerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleOrderScanned = (order: ScannedOrder) => {
    setSelectedOrder(order);
    setHistory(saveToHistory(order));
    setShowScanner(false);
  };

  const handleSetTable = (orderId: string, table: number) => {
    if (selectedOrder && selectedOrder.orderId === orderId) {
      setSelectedOrder({ ...selectedOrder, tableNumber: table });
    }
    const updated = loadHistory().map(o =>
      o.orderId === orderId ? { ...o, tableNumber: table } : o
    );
    localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
    setHistory(updated);
  };

  const handleRemoveFromHistory = (orderId: string) => {
    setHistory(removeFromHistory(orderId));
    if (selectedOrder?.orderId === orderId) setSelectedOrder(null);
  };

  // Сканер
  useEffect(() => {
    if (!showScanner) return;

    let html5QrCode: any = null;

    const startScanner = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        html5QrCode = new Html5Qrcode('qr-reader');
        scannerRef.current = html5QrCode;
        setScanning(true);

        await html5QrCode.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          async (decodedText: string) => {
            try {
              const data = JSON.parse(decodedText);
              if (data.orderId) {
                await html5QrCode.stop();
                setScanning(false);
                handleOrderScanned(data as ScannedOrder);
              } else if (data.id && data.name && data.phone) {
                // Client QR card — record restaurant visit
                await html5QrCode.stop();
                setScanning(false);
                try {
                  await fetch('/api/restaurant-visit', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id: data.id, name: data.name, phone: data.phone }),
                  });
                } catch {}
                setSelectedOrder({
                  orderId: `visit-${Date.now()}`,
                  date: new Date().toISOString(),
                  client: data.name,
                  phone: data.phone,
                  clientId: String(data.id),
                  items: [],
                  total: 0,
                  bonusUsed: 0,
                  deliveryType: 'В зале',
                  paymentType: '-',
                  status: 'confirmed',
                });
                setShowScanner(false);
              } else {
                setError('QR не содержит данных заказа');
              }
            } catch {
              setError('Не удалось распознать QR-код');
            }
          },
          () => {}
        );
      } catch (err: any) {
        setError('Не удалось запустить камеру: ' + (err.message || err));
        setShowManual(true);
      }
    };

    startScanner();

    return () => {
      if (html5QrCode && html5QrCode.isScanning) {
        html5QrCode.stop().catch(() => {});
      }
    };
  }, [showScanner]);

  const handleManualSubmit = () => {
    const input = manualInput.trim();
    try {
      const data = JSON.parse(input);
      if (data && data.orderId) {
        handleOrderScanned(data as ScannedOrder);
        setManualInput('');
        return;
      }
    } catch { /* не JSON */ }
    setError('Вставьте данные QR-кода заказа (JSON)');
  };

  return (
    <div className="space-y-4">
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-white">Заказы</h1>
        <button
          onClick={() => navigate('/waiter')}
          className="p-2 text-slate-400 hover:text-white transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* ── Кнопка сканирования — всегда видна ─────────────── */}
      <button
        onClick={() => { setShowScanner(!showScanner); setSelectedOrder(null); setError(''); }}
        className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
        </svg>
        {showScanner ? 'Закрыть сканер' : 'Сканировать QR заказа'}
      </button>

      {/* ── Сканер (раскрывается) ──────────────────────────── */}
      {showScanner && (
        <div className="space-y-3">
          <div className="bg-slate-800/60 rounded-2xl border border-slate-700/50 overflow-hidden">
            <div id="qr-reader" ref={containerRef} className="w-full" style={{ minHeight: 300 }} />
            {!scanning && !showManual && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center text-slate-400">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent mx-auto mb-3" />
                  <p>Запуск камеры...</p>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => setShowManual(!showManual)}
            className="w-full py-2 text-sm text-emerald-400 hover:text-emerald-300 transition-colors"
          >
            {showManual ? 'Скрыть ввод вручную' : 'Ввести данные вручную'}
          </button>

          {showManual && (
            <div className="bg-slate-800/60 rounded-xl border border-slate-700/50 p-4 space-y-3">
              <input
                type="text"
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value)}
                placeholder="JSON данные заказа"
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
              />
              <button
                onClick={handleManualSubmit}
                disabled={!manualInput.trim()}
                className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl font-medium transition-colors"
              >
                Показать заказ
              </button>
            </div>
          )}

          <p className="text-center text-slate-500 text-sm">
            Наведите камеру на QR-код заказа гостя
          </p>
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm text-center">
          {error}
          <button onClick={() => setError('')} className="ml-2 underline">OK</button>
        </div>
      )}

      {/* ── Детали выбранного заказа ───────────────────────── */}
      {selectedOrder && (
        <OrderCard
          order={selectedOrder}
          onSetTable={handleSetTable}
          onClose={() => setSelectedOrder(null)}
        />
      )}

      {/* ── История — всегда видна ─────────────────────────── */}
      {history.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">
            История ({history.length})
          </h2>
          {history.map((order) => {
            const d = new Date(order.date);
            const isSelected = selectedOrder?.orderId === order.orderId;
            return (
              <div
                key={order.orderId + order.date}
                className={`bg-slate-800/60 rounded-xl border p-4 transition-colors ${
                  isSelected ? 'border-emerald-500/50' : 'border-slate-700/50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <button
                    onClick={() => setSelectedOrder(isSelected ? null : order)}
                    className="flex-1 text-left"
                  >
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-white font-bold">#{order.orderId}</span>
                      {order.tableNumber && (
                        <span className="px-2 py-0.5 bg-blue-500/20 border border-blue-500/30 text-blue-400 text-xs rounded-full font-medium">
                          Стол {order.tableNumber}
                        </span>
                      )}
                      <span className="text-emerald-400 font-semibold">{order.total} ₽</span>
                      <span className="text-slate-500 text-xs">{order.deliveryType}</span>
                    </div>
                    <p className="text-slate-400 text-sm">{order.client || order.phone || '—'}</p>
                    <p className="text-slate-500 text-xs">
                      {d.toLocaleDateString('ru-RU')} {d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </button>
                  <button
                    onClick={() => handleRemoveFromHistory(order.orderId)}
                    className="p-1 text-slate-600 hover:text-red-400 transition-colors ml-2 shrink-0"
                    title="Удалить"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {history.length === 0 && !showScanner && (
        <div className="text-center py-8">
          <p className="text-slate-500">Нет отсканированных заказов</p>
          <p className="text-slate-600 text-sm mt-1">Нажмите кнопку выше для сканирования</p>
        </div>
      )}
    </div>
  );
};

// ── Компонент карточки заказа ────────────────────────────────
function OrderCard({ order, onSetTable, onClose }: {
  order: ScannedOrder;
  onSetTable: (orderId: string, table: number) => void;
  onClose: () => void;
}) {
  const orderDate = new Date(order.date);
  const statusLabel = order.status === 'completed' ? 'Завершён' :
    order.status === 'confirmed' ? 'Подтверждён' :
    order.status === 'cancelled' ? 'Отменён' : 'В обработке';
  const statusColor = order.status === 'completed' ? 'text-blue-400 bg-blue-400/10 border-blue-400/30' :
    order.status === 'confirmed' ? 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30' :
    order.status === 'cancelled' ? 'text-red-400 bg-red-400/10 border-red-400/30' :
    'text-yellow-400 bg-yellow-400/10 border-yellow-400/30';

  return (
    <div className="bg-slate-800/40 rounded-2xl border border-emerald-500/30 p-4 space-y-3">
      {/* Заголовок */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-white font-bold text-lg">#{order.orderId}</span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusColor}`}>
            {statusLabel}
          </span>
        </div>
        <button onClick={onClose} className="p-1 text-slate-400 hover:text-white">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
      </div>

      {/* Стол */}
      <select
        value={order.tableNumber || ''}
        onChange={(e) => onSetTable(order.orderId, Number(e.target.value))}
        className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 text-lg font-medium"
      >
        <option value="">Выберите стол</option>
        {Array.from({ length: 20 }, (_, i) => i + 1).map(n => (
          <option key={n} value={n}>Стол {n}</option>
        ))}
      </select>

      {/* Клиент */}
      <div className="flex justify-between items-start">
        <div>
          <p className="text-white font-medium">{order.client || '—'}</p>
          {order.phone && <p className="text-slate-400 text-sm">{order.phone}</p>}
        </div>
        <div className="text-right">
          <p className="text-slate-400 text-xs">
            {orderDate.toLocaleDateString('ru-RU')} {orderDate.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>

      {/* Получение / оплата */}
      <div className="flex justify-between text-sm">
        <span className="text-slate-400">Получение: <span className="text-white">{order.deliveryType}</span></span>
        <span className="text-slate-400">Оплата: <span className="text-white">{order.paymentType}</span></span>
      </div>

      {/* Позиции */}
      <div className="border-t border-slate-700/50 pt-3 space-y-1">
        {order.items.map((item, i) => (
          <p key={i} className="text-white text-sm">{item}</p>
        ))}
      </div>

      {/* Итого */}
      <div className="border-t border-slate-700/50 pt-3">
        {order.bonusUsed > 0 && (
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-400">Бонусы</span>
            <span className="text-emerald-400">−{order.bonusUsed} ₽</span>
          </div>
        )}
        <div className="flex justify-between text-lg font-bold">
          <span className="text-white">Итого</span>
          <span className="text-emerald-400">{order.total} ₽</span>
        </div>
      </div>
    </div>
  );
}
